/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import log from "loglevel";

import { TarifficationFormType } from "@/components/features/ToDos/TarifficationPage/formType";
import { defaultDateFormat } from "@/core/date";
import {
    Request,
    RequestCategory,
    RequestPagination,
    SalaryForm,
    SalarySelfEmployed,
    SearchableRequest,
    TenYearsLiability,
} from "@/shared/domain";

import { getSpApi, mapToFormData, pagedRequestConfig, requestConfig } from "./apiRoot";
import { UrlManager } from "./UrlManager";

// Swagger: https://ibpcsrcdev01.portima.net/mybrokerapi/swagger/index.html

// Requests
export const requestMapper = (source: MyBrokerApi.UserRequest): Request => {
    return {
        ...source,
        policyholderId: Number(source.policyHolderId),
        documentsQuantity: source.userRequestDocument?.length ? source.userRequestDocument.length : 0,
        status: { id: source.status.id, name: source.status.id === "Unknown" ? "" : source.status.name },
    };
};

export const searchableRequestMapper = (source: Request[]): SearchableRequest[] => {
    return source?.map((request) => ({
        ...request,
        updateDatetimeFormatted: defaultDateFormat(request.updateDatetime),
    }));
};

export type RequestsQueryParameters = Paths.ApiRequest.Get.QueryParameters;

export type RequestsSortProperty = Paths.ApiRequest.Get.Parameters.SortProperty;

/**
 * Returns all Requests
 */
export const getRequests = async (
    requestParams: RequestsQueryParameters = {},
    category: string = RequestCategory.ALL,
): Promise<RequestPagination> => {
    if (category !== RequestCategory.ALL) {
        requestParams.filter = category as MyBrokerApi.UserRequestStatusFilter;
    }
    const axiosRequestConfig = pagedRequestConfig<RequestsQueryParameters>({ params: requestParams });
    const { data } = await getSpApi().get<MyBrokerApi.UserRequestPayload>(UrlManager.requests(), axiosRequestConfig);

    return { ...data, result: data?.result.map((item) => requestMapper(item)) } as RequestPagination;
};

export const getRequestsLazy = async (requestParams: RequestsQueryParameters = {}): Promise<RequestPagination> => {
    return getRequests(requestParams);
};

/**
 * Return the address
 */
export const getAddress = async (
    policyHolderId: number,
    ibpAclId: number,
): Promise<MyBrokerApi.AddressChangeRequest> => {
    const axiosRequestConfig = requestConfig<Paths.ApiPolicyholderAddress$PolicyHolderId.Get.QueryParameters>({
        params: { ibpAclId },
    });
    const { data } = await getSpApi().get<Paths.ApiPolicyholderAddress$PolicyHolderId.Get.Responses.$200>(
        UrlManager.policyholderAddress(policyHolderId),
        axiosRequestConfig,
    );
    return data;
};

/**
 * Post address change request to server
 */
export const requestAddressChange = async (request: {
    policyholderId: number;
    body: MyBrokerApi.AddressChangeRequest;
    params: {
        ibpAclId: number;
    };
}): Promise<MyBrokerApi.UserRequest> => {
    const { policyholderId, body, params } = request;

    const axiosRequestConfig = requestConfig({ params });

    const { data } = await getSpApi().post<Paths.ApiRequestPolicyholderAddress$PolicyHolderId.Post.Responses.$200>(
        UrlManager.requestPolicyholderAddress(policyholderId),
        body,
        axiosRequestConfig,
    );
    log.debug("requestAddressChange result", body);
    return data;
};

const mapRelations = (relations: MyBrokerApi.FamilyMembers) => {
    // eslint-disable-next-line no-param-reassign
    relations.pendingRelations = relations.pendingRelations.map((pendingRelation) => {
        return { ...pendingRelation, isPending: true };
    });
    return relations;
};

/**
 * Get the relation of the policyholder
 */
export const getRelations = async (policyHolderId: number): Promise<MyBrokerApi.FamilyMembers> => {
    const { data } = await getSpApi().get<MyBrokerApi.FamilyMembers>(UrlManager.policyholderRelation(policyHolderId));
    return mapRelations(data);
};

export const getPersonalDetails = async (
    policyHolderId: number,
    ibpAclId: number,
): Promise<MyBrokerApi.PolicyHolderPersonalDetails> => {
    const axiosRequestConfig = requestConfig<Paths.ApiPolicyholderPersonalDetails$PolicyHolderId.Get.QueryParameters>({
        params: { ibpAclId },
    });
    const { data } = await getSpApi().get<Paths.ApiPolicyholderPersonalDetails$PolicyHolderId.Get.Responses.$200>(
        UrlManager.policyholderPersonalDetails(policyHolderId),
        axiosRequestConfig,
    );
    return data;
};

export const updatePersonalDetails = async (request: {
    policyholderId: number;
    body: any;
    params: {
        ibpAclId: number;
    };
}): Promise<MyBrokerApi.UserRequest> => {
    const { policyholderId, body, params } = request;

    const axiosRequestConfig =
        requestConfig<Paths.ApiRequestPolicyholderPersonalDetails$PolicyHolderId.Post.QueryParameters>({
            params,
        });

    const { data } =
        await getSpApi().post<Paths.ApiRequestPolicyholderPersonalDetails$PolicyHolderId.Post.Responses.$200>(
            UrlManager.requestPolicyholderDetails(policyholderId),
            body,
            axiosRequestConfig,
        );

    log.debug("updated personal details");
    return data;
};

export const requestFamilyMember = async (request: {
    policyholderId: number;
    body: any;
    params: {
        ibpAclId: number;
        requestId: number;
        relationTypeId: string;
    };
}): Promise<MyBrokerApi.UserRequest> => {
    const { policyholderId, body, params } = request;

    const axiosRequestConfig = requestConfig<Paths.ApiRequestPolicyholderRelation$PolicyHolderId.Post.QueryParameters>({
        params: { ...params, requestId: `${params.requestId}` },
    });

    const { data } = await getSpApi().post<Paths.ApiRequestPolicyholderRelation$PolicyHolderId.Post.Responses.$200>(
        UrlManager.requestPolicyholderRelation(policyholderId),
        body,
        axiosRequestConfig,
    );
    log.debug("add family member success");
    return data;
};

export const getRelationDetails = async (
    relationId: number,
    policyHolderId: number,
    ibpAclId: number,
    typePartyId: number,
): Promise<MyBrokerApi.RelationDetails> => {
    const axiosRequestConfig =
        requestConfig<Paths.ApiPolicyholderRelationPersonalDetails$RelationDetailsId.Get.QueryParameters>({
            params: { policyHolderId, ibpAclId, typePartyId },
        });
    const { data } =
        await getSpApi().get<Paths.ApiPolicyholderRelationPersonalDetails$RelationDetailsId.Get.Responses.$200>(
            UrlManager.relationPersonalDetails(relationId),
            axiosRequestConfig,
        );
    return data;
};

export const updateRelationDetails = async (params: {
    policyHolderId: number;
    relationDetailsId: number;
    relationInternalId: number;
    internalpolicyholderId: number;
    typePartyId: number;
    ibpAclId: number;
    body: MyBrokerApi.PersonalDetails;
}): Promise<MyBrokerApi.UserRequest> => {
    const { body, policyHolderId, ...configs } = params;
    const axiosRequestConfig =
        requestConfig<Paths.ApiRequestPolicyholderRelationPersonalDetails$PolicyHolderId.Post.QueryParameters>({
            params: configs,
        });
    const { data } =
        await getSpApi().post<Paths.ApiRequestPolicyholderRelationPersonalDetails$PolicyHolderId.Post.Responses.$200>(
            UrlManager.requestPolicyholderRelationDetails(policyHolderId),
            body,
            axiosRequestConfig,
        );
    log.debug("updated personal details");
    return data;
};

export const reportClaim = async (reportClaimData): Promise<number> => {
    const data = mapToFormData(reportClaimData);

    const { status } = await getSpApi().post<Paths.ApiRequestClaimreport.Post.Responses.$204>(
        UrlManager.claimReport(),
        data,
    );
    return status;
};

export const reportSchoolClaim = async (params: {
    contractId: number;
    policyholderId: number;
    ibpAclId: number;
    schoolClaimReport: MyBrokerApi.SchoolClaim;
}): Promise<MyBrokerApi.UserRequest> => {
    const { schoolClaimReport, ...configs } = params;
    const axiosRequestConfig = requestConfig<Paths.ApiRequestSchoolclaim.Post.QueryParameters>({
        params: configs,
    });
    const { data } = await getSpApi().post<Paths.ApiRequestSchoolclaim.Post.Responses.$200>(
        UrlManager.schoolClaim(),
        schoolClaimReport,
        axiosRequestConfig,
    );
    return data;
};

export const submitLiabilityForm = async (params: {
    policyholderId: number;
    liabilityForm: TenYearsLiability;
}): Promise<MyBrokerApi.UserRequest> => {
    const { liabilityForm, policyholderId, ...configs } = params;
    const axiosRequestConfig = requestConfig({
        params: configs,
    });
    const { data } = await getSpApi().post<Paths.ApiRequestTenyearsliability$PolicyHolderId.Post.Responses.$200>(
        UrlManager.liabilityForm(policyholderId),
        liabilityForm,
        axiosRequestConfig,
    );
    return data;
};

export const submitSalaryForm = async (params: { salaryForm: SalaryForm }): Promise<[number]> => {
    const { salaryForm, ...configs } = params;
    const axiosRequestConfig = requestConfig({ params: configs });
    const { status } = await getSpApi().post(UrlManager.postSalaryForm(), salaryForm, axiosRequestConfig);
    return [status];
};

export const getSalaryForm = async (policyholderId: number): Promise<SalarySelfEmployed> => {
    const { data } = await getSpApi().get<SalarySelfEmployed>(UrlManager.getSalaryForm(policyholderId));
    return data;
};

/**
 * Returns a victim school claim login by proeco
 */
export const getVictimSchoolClaimProecoLogin = async (
    context: string,
): Promise<MyBrokerApi.SchoolClaimVictimRequest> => {
    const axiosRequestConfig = requestConfig<Paths.ApiRequestSchoolclaimVictim.Get.QueryParameters>({
        params: { context },
    });
    const { data } = await getSpApi().get<MyBrokerApi.SchoolClaimVictimRequest>(
        UrlManager.schoolClaimVictimDetails(),
        axiosRequestConfig,
    );
    return data;
};

export const submitCustomerCardForm = async (
    customerCard: MyBrokerApi.CustomerCardRequest,
): Promise<MyBrokerApi.UserRequest> => {
    const { data } = await getSpApi().post<Paths.ApiRequestCustomercard.Post.Responses.$200>(
        UrlManager.requestCustomerCard(),
        customerCard,
    );
    return data;
};

/**
 * Request tariffication
 */

export const requestTariffication = async (tarifficationData: TarifficationFormType): Promise<void> => {
    const { config, ...values } = tarifficationData;

    const data = mapToFormData(values);

    await getSpApi().post<Paths.ApiRequestVehicleTariffication$PolicyholderId.Post.Responses.$204>(
        UrlManager.requestTariffication(tarifficationData.policyholder.id),
        data,
    );
};

/**
 * Request company data
 */

export const requestCompanyDataVerification = async (
    companyDataVerification: MyBrokerApi.MoralPersonDetailsForm,
): Promise<void> => {
    await getSpApi().post<Paths.ApiRequestPolicyholderMoralDetails.Post.Responses.$204>(
        UrlManager.requestCompanyDataVerification(),
        companyDataVerification,
    );
};
